import UniversalWebView from '@/components/UniversalWebView'
import { getAuthToken } from '@/lib/api/authToken'
import { appendQueryParams } from '@/lib/utils/url'
import { setHeaderImageEditResult, setNavigatedAway } from '@/redux/slices/formAIGenerationSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { useRouter } from 'expo-router'
import { useCallback, useMemo } from 'react'

type AIGeneratedFormPreviewWebviewProps = {
  url: string
}

type AIGeneratedFormPreviewWebviewMessage = {
  event: 'AI_FORM'
  details: {
    replaceHeaderImage?: { oldUrl: string; newUrl: string }
    redirectToFormBuilder?: { formToken: string; dismissConversation?: boolean }
    goBack?: true
  }
}

export default function AIGeneratedFormPreviewWebview({ url }: AIGeneratedFormPreviewWebviewProps) {
  const router = useRouter()
  const dispatch = useAppDispatch()

  const headerImageEditResult = useAppSelector(state => state.formAIGeneration.headerImageEditResult)

  const authedUrl = useMemo(() => {
    const token = getAuthToken()
    return appendQueryParams(url, { m_t: token })
  }, [url])

  const handleMessage = useCallback(
    (data: AIGeneratedFormPreviewWebviewMessage) => {
      if (data.event !== 'AI_FORM') return

      const { replaceHeaderImage, redirectToFormBuilder, goBack } = data.details

      if (goBack) router.back()

      if (redirectToFormBuilder) {
        const { formToken, dismissConversation } = redirectToFormBuilder
        dispatch(setNavigatedAway(true))
        if (dismissConversation) router.back()
        router.replace(`/forms/${formToken}/edit`)
      }

      if (replaceHeaderImage) {
        const originalUrlsMap = new Map(
          Object.entries(headerImageEditResult).map(([originalUrl, replacedUrl]) => [replacedUrl, originalUrl])
        )
        const newHeaderImageReplacements = { ...headerImageEditResult }
        const newUrl = replaceHeaderImage.newUrl
        const originalUrl = originalUrlsMap.get(replaceHeaderImage.oldUrl) || replaceHeaderImage.oldUrl
        newHeaderImageReplacements[originalUrl] = newUrl
        dispatch(setHeaderImageEditResult(newHeaderImageReplacements))
      }
    },
    [dispatch, headerImageEditResult, router]
  )

  return (
    <UniversalWebView
      source={{ uri: authedUrl }}
      injectedCSS="body { padding-top: env(safe-area-inset-top) !important; }"
      contentInsetAdjustmentBehavior="never"
      onMessage={handleMessage}
      className='flex-1'
    />
  )
}
